import { useState, useEffect } from "react";
import { formatStamp } from "../lib/richText";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getProjects, updateProjectStatus, deleteProject } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Archive, Loader2, Search, MoreHorizontal, Eye, RotateCcw, ShoppingCart, Trash2 } from "lucide-react";

const PRODUCT_LABELS = {
  marketing: "Marketing", web_design: "Web Design", seo_aeo: "SEO/AEO",
  prospect_tools: "ProspectID/Max", digital_marketing: "Digital Marketing", ai_tools: "AI Tools",
};

export default function ArchivedOrdersPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchProjects = async () => {
    try {
      const { data } = await getProjects();
      // Show ONLY projects explicitly marked as archived lifecycle
      // (avoid showing monthly_active projects that happen to have status=completed)
      setProjects(data.filter((p) => {
        if (p.lifecycle_stage === "archived") return true;
        // Legacy: completed/cancelled projects with no lifecycle_stage set
        if (!p.lifecycle_stage && ["completed", "cancelled"].includes(p.status)) return true;
        return false;
      }));
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  const handleReactivate = async (projectId) => {
    try { await updateProjectStatus(projectId, "in_progress"); fetchProjects(); } catch (e) { console.error(e); }
  };

  const isAdmin = ["super_admin", "manager"].includes(user?.role);

  const handleDeleteOrder = async (projectId, clientLabel) => {
    if (!window.confirm(`Permanently delete this order for ${clientLabel || "this client"}? Related tasks and comments will also be removed. This cannot be undone.`)) return;
    try {
      await deleteProject(projectId);
      toast.success("Order deleted");
      fetchProjects();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete order");
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const getProductLabel = (p) => (p.product_types || [p.product_type]).map((t) => PRODUCT_LABELS[t] || t).join(" + ");

  const filtered = projects.filter((p) =>
    p.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.client_company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    getProductLabel(p).toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group by client
  const groupedByClient = {};
  filtered.forEach((p) => {
    const key = p.client_name || "Unknown";
    if (!groupedByClient[key]) groupedByClient[key] = { client_company: p.client_company, agency_name: p.agency_name || "", orders: [] };
    groupedByClient[key].orders.push(p);
  });

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="archived-orders-page">
      <div>
        <h1 className="font-outfit text-3xl font-bold tracking-tight">Archived Clients</h1>
        <p className="text-muted-foreground mt-1">{filtered.length} archived clients — re-order a new campaign anytime</p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search by client name..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="search-archived-input" />
      </div>

      {filtered.length === 0 ? (
        <Card className="border"><CardContent className="flex flex-col items-center justify-center py-16">
          <Archive className="h-16 w-16 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium">No archived orders</h3>
          <p className="text-muted-foreground text-sm">Completed orders will appear here</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedByClient).map(([clientName, group]) => (
            <Card key={clientName} className="border">
              <CardHeader className="pb-3">
                <CardTitle className="font-outfit text-lg flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-zinc-200 flex items-center justify-center text-zinc-700 text-sm font-semibold">
                    {clientName.charAt(0).toUpperCase()}
                  </div>
                  {clientName}
                  {group.agency_name && <span className="text-sm font-normal text-muted-foreground">/ {group.agency_name}</span>}
                  {group.client_company && <span className="text-xs font-normal text-muted-foreground ml-1">({group.client_company})</span>}
                  <Badge variant="outline" className="ml-auto">{group.orders.length} order{group.orders.length > 1 ? "s" : ""}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Products</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                    {user?.role !== "rep" && <TableHead>Rep</TableHead>}
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {group.orders.map((order) => (
                      <TableRow key={order.id} className="table-row-hover">
                        <TableCell>
                          <Link to={`/orders/${order.id}`} className="flex flex-wrap gap-1" data-testid={`order-link-${order.id}`}>
                            {(order.product_types || [order.product_type]).map((t) => (
                              <Badge key={t} variant="outline" className="text-xs cursor-pointer hover:bg-primary/10">{PRODUCT_LABELS[t] || t}</Badge>
                            ))}
                          </Link>
                        </TableCell>
                        <TableCell><Badge className="bg-green-100 text-green-900">{order.status.replace("_", " ")}</Badge></TableCell>
                        <TableCell className="text-sm text-muted-foreground" data-testid={`archived-submitted-${order.id}`}>{formatStamp(order.created_at)}</TableCell>
                        {user?.role !== "rep" && <TableCell className="text-muted-foreground text-sm">{order.rep_name || "—"}</TableCell>}
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {["manager", "rep"].includes(user?.role) && (
                              <Link to={`/orders/new?client=${order.client_id}`}>
                                <Button variant="outline" size="sm" data-testid={`reorder-${order.id}`}>
                                  <ShoppingCart className="mr-1.5 h-3.5 w-3.5" /> Re-order
                                </Button>
                              </Link>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem asChild><Link to={`/orders/${order.id}`}><Eye className="mr-2 h-4 w-4" />View Details</Link></DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleReactivate(order.id)}>
                                  <RotateCcw className="mr-2 h-4 w-4" />Reactivate Order
                                </DropdownMenuItem>
                                {isAdmin && (
                                  <DropdownMenuItem
                                    onClick={() => handleDeleteOrder(order.id, order.client_company || order.client_name)}
                                    className="text-destructive focus:text-destructive"
                                    data-testid={`delete-archived-${order.id}`}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />Delete Order
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
